import React, { useEffect } from 'react';
/**
 * `SummaryPage` renders the dealer journey summary/checkout step.
 *
 * Responsibilities:
 * - Renders the shared `Summary` component for vehicle, personal details, and
 *   selected products.
 * - Requires customer acceptance before allowing continuation.
 * - Routes forward based on selected payment methods:
 *   - single payment only -> `SinglePayment`
 *   - payment assist only -> initiates redirect via `PaymentApi.beginPaymentAssistRequest`
 *   - otherwise -> `DirectDebit`
 */
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Journey, PageRelativePath } from '../../consts/Common';
import './SummaryPage.scss';
import PageHeader from '../common/PageHeader';
import { PaymentMethod } from '../common/Constants';
import { ButtonContinue } from '../common/Button/ButtonContinue';
import { ButtonBack } from '../common/Button/ButtonBack';
import Summary, { SummaryCustomerAcceptance } from '../common/Summary/Summary';
import { useJourney } from './Layout';
import { useTranslation } from 'react-i18next';
import { PaymentApi } from '../../api/PaymentApi';
import {
  Totals,
  PaymentAssistCustomer,
  PaymentAssistRequest,
  PaymentAssistBeginResponse
} from '../common/CommonTypes';
import { getSelectedProducts, calculateTotals } from '../common/ProductMethods';
import { useAppSelector } from '../../app-state/Hooks';

const SummaryPage = (): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const {
    journey,
    onUpdateProducts,
    onUpdateAgreedTermsAndConditions,
    onProductDocumentsDownloaded,
    onPageChange
  } = useJourney();

  const {
    vehicle,
    personalDetail,
    products,
    agreedTermsAndConditions,
    productDocumentsDownloaded,
    singlePaymentDetails
  } = journey;

  const site = useAppSelector((s) => s); // TODO: consider render optimization
  const appConfig = useAppSelector((s) => s.appConfig);

  const cms = site.content.summaryPage;

  const navigate = useNavigate();

  const { t } = useTranslation();

  const onBack = () => {
    navigate(PageRelativePath.PersonalDetails);
  };

  const isOnlySinglePaymentProducts = () => {
    const selectedProducts = products.filter((p) => p.isSelected);

    return selectedProducts.every(
      (p) => p.selectedPaymentMethod === PaymentMethod.Single
    );
  };

  const isOnlyPaymentAssistProducts = () => {
    const selectedProducts = products.filter((p) => p.isSelected);

    return selectedProducts.every(
      (p) => p.selectedPaymentMethod === PaymentMethod.PaymentAssist
    );
  };

  const onContinue = () => {
    if (isOnlySinglePaymentProducts()) {
      navigate(PageRelativePath.SinglePayment);
    } else if (isOnlyPaymentAssistProducts()) {
      callPaymentAssistRedirect();
    } else {
      navigate(PageRelativePath.DirectDebit);
    }
  };

  const selectedProducts = getSelectedProducts(products);
  const totals: Totals = calculateTotals(selectedProducts, 1);

  const callPaymentAssistRedirect = () => {
    const token = journey.token || site.user.token;

    const { billingAddress } = singlePaymentDetails;

    const selectedVariant = selectedProducts[0].productVariants.find(
      (v) => v.isSelected
    );

    const paymentInfo: PaymentAssistCustomer = {
      amount: totals.selectedTotalPrice,
      productVariant: selectedVariant?.variantId ?? '',
      surname: personalDetail.surname,
      firstName: personalDetail.firstName,
      address1: billingAddress.addressLine1 ?? '',
      address2: billingAddress.addressLine2 ?? '',
      city: billingAddress.townOrCity ?? '',
      postCode: billingAddress.postCode ?? '',
      county: billingAddress.region ?? '',
      phone: personalDetail.phone,
      email: personalDetail.email,
      vrn: vehicle?.vehicleDetails?.vrn ?? ''
    };

    const paymentAssistRequest: PaymentAssistRequest = {
      paymentInfo,
      site: {
        user: site.user,
        brand: site.brand,
        content: site.content,
        ...appConfig
      },
      journey,
      journeyType: appConfig.journeyType
    };

    const controller = new AbortController();

    const successCallback = (data: PaymentAssistBeginResponse) => {
      window.localStorage.setItem('redirectToPA', 'true');
      window.location.href = data.url;
    };

    const failureCallback = () => {
      console.log('failure');
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      navigate(PageRelativePath.PaymentAssistFailure, {
        relative: 'path'
      });
    };

    new PaymentApi().beginPaymentAssistRequest(
      paymentAssistRequest,
      token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const customerAcceptanceText: SummaryCustomerAcceptance = {
    sections: cms.acceptanceText
  };

  return (
    <>
      <Helmet>
        <title>{t('page-title.summary')}</title>
      </Helmet>

      <div>
        <PageHeader
          title={cms.title}
          description={cms.description}
          information={cms.information}
          warning={cms.warning}
          descriptionClass='p2'
        />
        <Summary
          vehicle={vehicle}
          personalDetail={personalDetail}
          products={products}
          agreedTermsAndConditions={agreedTermsAndConditions}
          onUpdateAgreedTermsAndConditions={onUpdateAgreedTermsAndConditions}
          onUpdateProducts={onUpdateProducts}
          onPageChange={onPageChange}
          journeyType={Journey.DealerJourney}
          policyStartDate={new Date()}
          error=''
          isRenewals={false}
          showTermsAndConditions={true}
          personalDetailsPageUrl={PageRelativePath.PersonalDetails}
          vehiclePageUrl={PageRelativePath.VehicleDetails}
          productPageUrl={PageRelativePath.Products}
          createPlanPageUrl={PageRelativePath.CreatePlan}
          customerAcceptanceText={customerAcceptanceText}
          customerAcceptanceSectionExpandedOnLoad={true}
          customerAcceptanceTickboxText={cms.acceptanceTickboxText}
          culture={appConfig.cultureCode}
          countryCode={appConfig.countryCode}
          renewalMarketingTickboxText={cms.consentToRenewalMarketingTickboxText}
          phoneNumberLabel={site.content.customerPage.phoneNumberLabel}
          productDocumentsPaths={[]}
          productDocumentsDownloaded={productDocumentsDownloaded || false}
          onUpdateProductDocumentsDownloaded={onProductDocumentsDownloaded}
        />
        <div className='flex-row form-nav'>
          <ButtonBack
            name='btnBack'
            onClick={onBack}
            text={t('customer-button.back-buttont-text')}
          />
          <ButtonContinue
            name='btn-checkout'
            text={t('customer-button.button-continue-text')}
            onClick={onContinue}
            disabled={!agreedTermsAndConditions}
          />
        </div>
      </div>
    </>
  );
};

export default SummaryPage;
